import { Controller } from "@hotwired/stimulus"

// chat--add-users-modal on `_add_admins.haml` and `_add_members.haml`.
// Parameterized via values: findUsersPath, addUsersPath, role ("admins"|"members").
export default class extends Controller {
  static values = {
    findUsersPath: String,
    addUsersPath: String,
    role: String,
  }

  static targets = ["searchInput", "usersList", "placeholder", "saveBtn"]

  connect() {
    this.selectedUsers = []
    this.foundUsers = []
    this.isLoading = false
    this.allUsersLoaded = false
    this.loadUsersLimit = 100
    this.activeRequestController = null

    this.#bindCustomEvents()
    $(this.element).on("hidden.bs.modal.addUsersModal", () => this.#onModalHidden())
  }

  disconnect() {
    this.#abortRequest()
    $(this.element).off(".addUsersModal")
  }

  save(event) {
    event.preventDefault()
    if (this.selectedUsers.length === 0) {
      $(this.element).modal("hide")
      return
    }

    const userIds = this.selectedUsers.map((u) => u.id)
    $(this.element).modal("hide")

    const bodyKey = this.roleValue === "admins" ? "admins_ids" : "occupants_ids"

    fetch(this.addUsersPathValue, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ [bodyKey]: userIds }),
    })
      .then((response) => {
        if (!response.ok) throw new Error("Forbidden")
        return response.json()
      })
      .then((response) => {
        if (response.success) {
          this.dispatch("usersAdded")
        }
      })
      .catch((error) => {
        console.error(`Error adding ${this.roleValue}:`, error)
      })
  }

  // --- private ---

  #bindCustomEvents() {
    const search = this.searchInputTarget
    search.addEventListener("search-string-changed", (e) => this.#onUserInput(e))
    search.addEventListener("dropdown-item-click", (e) => this.#userSelected(e))
    search.addEventListener("dropdown-scrolled-to-end", () => this.#loadMore())

    if (this.hasUsersListTarget) {
      this.usersListTarget.addEventListener("user-removed", (e) => this.#userRemoved(e))
    }
  }

  #onModalHidden() {
    this.#abortRequest()
    this.selectedUsers = []
    this.foundUsers = []
    this.allUsersLoaded = false
    if (this.hasUsersListTarget && this.usersListTarget.clear) this.usersListTarget.clear()
    if (this.searchInputTarget.clear) this.searchInputTarget.clear()
    if (!this.saveBtnTarget.classList.contains("inactive")) {
      this.saveBtnTarget.classList.add("inactive")
    }
    this.#togglePlaceholder()
  }

  #onUserInput(event) {
    this.#abortRequest()
    const value = event.detail.value
    if (!value || value.length === 0) return

    this.foundUsers = []
    this.allUsersLoaded = false
    this.isLoading = true
    this.activeRequestController = new AbortController()

    fetch(this.findUsersPathValue, {
      signal: this.activeRequestController.signal,
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query: value, skip: 0, limit: this.loadUsersLimit }),
    })
      .then((r) => { if (!r.ok) throw new Error("Forbidden"); return r.json() })
      .then((response) => {
        this.isLoading = false
        if (!response.success) return

        let users = response.users
        if (users.length < this.loadUsersLimit) this.allUsersLoaded = true

        users = users.map((u) => ({ id: u.id, value: u.name + this.#userIdString(u.id) }))
        users = users.filter((u) => !this.selectedUsers.some((s) => s.id == u.id))
        this.foundUsers = users
        this.searchInputTarget.setData(users)
        if (!this.allUsersLoaded && users.length <= 3) this.#loadMore()
      })
      .catch((error) => {
        if (error.name === "AbortError") return
        this.isLoading = false
        this.allUsersLoaded = true
        this.searchInputTarget.setData([])
      })
  }

  #loadMore() {
    this.#abortRequest()
    if (this.allUsersLoaded || this.isLoading) return

    const value = this.searchInputTarget.getText ? this.searchInputTarget.getText() : ""
    if (value.length === 0) return

    this.isLoading = true
    this.activeRequestController = new AbortController()

    fetch(this.findUsersPathValue, {
      signal: this.activeRequestController.signal,
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query: value, skip: this.foundUsers.length, limit: this.loadUsersLimit }),
    })
      .then((r) => { if (!r.ok) throw new Error("Forbidden"); return r.json() })
      .then((response) => {
        this.isLoading = false
        if (!response.success) return

        let users = response.users
        if (users.length < this.loadUsersLimit) this.allUsersLoaded = true

        users = users.map((u) => ({ id: u.id, value: u.name + this.#userIdString(u.id) }))
        users = users.filter((u) => !this.selectedUsers.some((s) => s.id == u.id))
        if (users.length === 0) { this.allUsersLoaded = true; return }

        this.foundUsers.push(...users)
        this.searchInputTarget.setData(this.foundUsers)
      })
      .catch((error) => {
        if (error.name === "AbortError") return
        this.allUsersLoaded = true
        this.isLoading = false
      })
  }

  #userSelected(event) {
    this.foundUsers = []
    const { value, id } = event.detail
    if (this.selectedUsers.some((u) => u.id === id)) return

    const cleanValue = value.replace(this.#userIdString(id), "")
    this.selectedUsers.push({ id, value: cleanValue })
    if (this.hasUsersListTarget && this.usersListTarget.addUser) {
      this.usersListTarget.addUser({ id, value: cleanValue })
    }
    this.#togglePlaceholder()
    this.#updateSaveButton()
  }

  #userRemoved(event) {
    const { id } = event.detail
    this.selectedUsers = this.selectedUsers.filter((u) => u.id !== id)
    this.#togglePlaceholder()
    this.#updateSaveButton()
  }

  #togglePlaceholder() {
    if (!this.hasPlaceholderTarget || !this.hasUsersListTarget) return
    if (this.selectedUsers.length > 0) {
      this.placeholderTarget.classList.add("hidden")
      this.usersListTarget.classList.remove("hidden")
    } else {
      this.placeholderTarget.classList.remove("hidden")
      this.usersListTarget.classList.add("hidden")
    }
  }

  #updateSaveButton() {
    if (!this.hasSaveBtnTarget) return
    if (this.selectedUsers.length > 0) {
      this.saveBtnTarget.classList.remove("inactive")
    } else {
      this.saveBtnTarget.classList.add("inactive")
    }
  }

  #abortRequest() {
    if (this.activeRequestController) {
      this.activeRequestController.abort()
      this.activeRequestController = null
    }
  }

  #userIdString(id) {
    return ` (ID: ${id})`
  }
}
